// src/components/layout/Navbar/NavbarUi.tsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, Sun, Moon, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "../../../data/navData";
import { useScrolled } from "../../../hooks/useWindowSize";
import { useThemeContext } from "../../../context/ThemeContext";
import type { NavLink } from "../../../types/company.types";
import ContainerUi from "../../ui/Container/ContainerUi";
import { smoothScrollToTop } from "../../../utils/smoothScroll";
import { ForetekNavbarLogo } from "../../brand/ForetekNavbarLogo";


const NavbarUi: React.FC = React.memo(() => {
  const scrolled = useScrolled(24);
  const [open, setOpen] = useState(false);
  const [activeDD, setActiveDD] = useState<string | null>(null);
  const [mobileDD, setMobileDD] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isDark, toggleTheme } = useThemeContext();

  useEffect(() => {
    setOpen(false);
    setActiveDD(null);
  }, [location.pathname]);

  // Cleanup timer on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Keyboard Escape closing and Focus Trapping
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        if (!menuRef.current) return;
        const focusables = menuRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // Move focus to first element when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const firstLink = menuRef.current?.querySelector<HTMLElement>("a");
        firstLink?.focus();
      }, 80);
    }
  }, [open]);

  const openDD = (l: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setActiveDD(l);
  };
  const closeDD = () => {
    timerRef.current = setTimeout(() => setActiveDD(null), 130);
  };
  const isActive = (p: string) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p);

  return (
    <>
      {/* Main nav with dynamic scroll-linked scale and layout adjustments */}
      <nav
        className={`fixed left-1/2 -translate-x-1/2 z-[100] border shadow-2xl transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${scrolled
          ? "top-2.5 w-[92%] md:w-[77.5%] rounded-xl backdrop-blur-2xl"
          : "top-4 w-[94%] md:w-[80%] rounded-2xl backdrop-blur-xl"
          }`}
        style={{
          backgroundColor: "var(--nav-bg)",
          borderColor: "var(--nav-border)",
          willChange: 'transform, top, width, border-radius, border-color, background-color',
          transform: 'translateX(-50%) translateZ(0)',
          maxWidth: scrolled ? '1392px' : '1440px',
        } as React.CSSProperties}
      >
        {/* Top gradient line when scrolled */}
        <div
          className="absolute top-0 left-0 right-0 h-px transition-opacity duration-300"
          style={{
            background: `linear-gradient(90deg, transparent, var(--blue), var(--violet), transparent)`,
            opacity: scrolled ? 1 : 0,
          }}
        />

        <ContainerUi className="md:w-full md:px-6">
          <div className="flex items-center justify-between h-12 md:h-14">
            {/* Logo */}
            <ForetekNavbarLogo isScrolled={scrolled} onClick={smoothScrollToTop} />

            {/* Desktop links */}
            <div
              className="hidden lg:flex items-center gap-1 relative"
              onMouseLeave={() => setHoveredLink(null)}
            >
              {NAV_LINKS.map((link: NavLink) => {
                const isCurrentActive = isActive(link.path);
                return (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => {
                      setHoveredLink(link.label);
                      if (link.dropdown) openDD(link.label);
                    }}
                    onMouseLeave={() => {
                      if (link.dropdown) closeDD();
                    }}
                  >
                    <Link
                      to={link.path}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none outline-none relative"
                      style={{
                        color: isCurrentActive ? "var(--blue)" : "var(--t2)",
                      }}
                    >
                      {/* Floating hover bubble highlight */}
                      {hoveredLink === link.label && (
                        <motion.div
                          layoutId="nav-hover-pill"
                          className="absolute inset-0 rounded-lg bg-[var(--hover-bg)]"
                          style={{ zIndex: 0 }}
                          transition={{ type: "spring", stiffness: 350, damping: 28 }}
                        />
                      )}

                      <span className="relative z-10 flex items-center gap-1">
                        {link.label}
                        {link.dropdown && (
                          <ChevronDown
                            size={12}
                            className={`transition-transform duration-200 ${activeDD === link.label ? "rotate-180" : ""}`}
                          />
                        )}
                      </span>

                      {/* Active indicator bottom line */}
                      {isCurrentActive && (
                        <motion.div
                          layoutId="nav-active-line"
                          className="absolute bottom-0 left-3.5 right-3.5 h-[2px] rounded-full"
                          style={{ background: 'var(--blue)', zIndex: 10 }}
                          transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        />
                      )}
                    </Link>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {link.dropdown && activeDD === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.97 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-full left-0 pt-2 z-[110]"
                          onMouseEnter={() => openDD(link.label)}
                          onMouseLeave={closeDD}
                        >
                          <div
                            className="glass-card rounded-xl overflow-hidden w-64 dropdown-panel"
                            style={{ boxShadow: "var(--shadow-md)" }}
                          >
                            {link.dropdown.map((item: NavLink, i: number) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-center justify-between px-4 py-3 text-sm transition-all duration-200 group/item hover:bg-[var(--hover-bg)] hover:text-[var(--t1)]"
                                style={{
                                  color: "var(--t2)",
                                  borderTop:
                                    i > 0 ? "1px solid var(--divider)" : "none",
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  <span
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-transform group-hover/item:scale-125"
                                    style={{ background: "var(--blue)" }}
                                  />
                                  <span className="transition-transform group-hover/item:translate-x-1 duration-200">
                                    {item.label}
                                  </span>
                                </div>
                                <ArrowRight
                                  size={12}
                                  className="opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-200"
                                  style={{ color: 'var(--blue)' }}
                                />
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* CTA + Theme Toggle + Hamburger */}
            <div className="flex items-center gap-2">
              {/* ── Theme Toggle Button ── */}
              <motion.button
                onClick={toggleTheme}
                className="theme-toggle-btn"
                whileTap={{ scale: 0.9 }}
                title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                aria-label={
                  isDark ? "Switch to Light Mode" : "Switch to Dark Mode"
                }
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isDark ? (
                    <motion.span
                      key="sun"
                      initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                      animate={{ rotate: 0, opacity: 1, scale: 1 }}
                      exit={{ rotate: 90, opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.22 }}
                      style={{ display: "flex" }}
                    >
                      <Sun size={15} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="moon"
                      initial={{ rotate: 90, opacity: 0, scale: 0.5 }}
                      animate={{ rotate: 0, opacity: 1, scale: 1 }}
                      exit={{ rotate: -90, opacity: 0, scale: 0.5 }}
                      transition={{ duration: 0.22 }}
                      style={{ display: "flex" }}
                    >
                      <Moon size={15} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>

              <button
                ref={triggerRef}
                onClick={() => setOpen(!open)}
                className="lg:hidden w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
                aria-label="Toggle navigation menu"
                aria-expanded={open}
                aria-controls="mobile-navigation-menu"
                style={{ color: "var(--t2)", backgroundColor: "transparent" }}
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </ContainerUi>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            id="mobile-navigation-menu"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed inset-0 z-[90] lg:hidden mobile-menu-bg"
            style={{ paddingTop: 64 }}
          >
            <div className="px-6 py-4 flex flex-col gap-1 overflow-y-auto h-full pb-24">


              {NAV_LINKS.map((link: NavLink) => (
                <div key={link.label}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={link.path}
                      onClick={() => setOpen(false)}
                      className="flex-1 py-3.5 text-base font-medium transition-colors"
                      style={{
                        color: isActive(link.path)
                          ? "var(--blue)"
                          : "var(--t1)",
                      }}
                    >
                      {link.label}
                    </Link>
                    {link.dropdown && (
                      <button
                        onClick={() =>
                          setMobileDD(
                            mobileDD === link.label ? null : link.label,
                          )
                        }
                        className="p-2 rounded-lg"
                        style={{ color: "var(--t3)" }}
                      >
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${mobileDD === link.label ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                  </div>
                  {link.dropdown && mobileDD === link.label && (
                    <div
                      className="pl-4 border-l-2 mb-2"
                      style={{ borderColor: "var(--blue)" }}
                    >
                      {link.dropdown.map((item: NavLink) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setOpen(false)}
                          className="block py-2.5 text-sm"
                          style={{ color: "var(--t3)" }}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                  <div
                    className="h-px"
                    style={{ background: "var(--divider)" }}
                  />
                </div>
              ))}

              {/* Mobile CTA */}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export default NavbarUi;
